/**
 * Vid2MP3 - Core Audio Extraction Engine (TypeScript)
 */

export interface ExtractAudioOptions {
  videoFile: File | null;
  videoUrl: string | null;
  outputFormat: string;
  useTimeRange: boolean;
  startTime: number;
  endTime: number;
  onProgress?: (percent: number) => void;
}

export interface ExtractedAudioResult {
  blob: Blob;
  url: string;
  filename: string;
}

export class ExtractorEngine {

  private static encodeWAV(channels: Float32Array[], sampleRate: number, numChannels: number): ArrayBuffer {
    const numFrames = channels[0].length;
    const bytesPerSample = 2; // 16-bit PCM
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = numFrames * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    function writeString(offset: number, string: string): void {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    }

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    let offset = 44;
    for (let i = 0; i < numFrames; i++) {
      for (let channel = 0; channel < numChannels; channel++) {
        let sample = Math.max(-1, Math.min(1, channels[channel][i]));
        sample = (sample < 0 ? sample * 0x8000 : sample * 0x7FFF);
        view.setInt16(offset, sample, true);
        offset += 2;
      }
    }

    return buffer;
  }

  public static async extractAudio(options: ExtractAudioOptions): Promise<ExtractedAudioResult> {
    const { videoFile, videoUrl, outputFormat, useTimeRange, startTime, endTime, onProgress } = options;

    if (onProgress) onProgress(15);

    let arrayBuffer: ArrayBuffer;
    if (videoFile) {
      arrayBuffer = await videoFile.arrayBuffer();
    } else if (videoUrl) {
      const res = await fetch(videoUrl);
      arrayBuffer = await res.arrayBuffer();
    } else {
      throw new Error("No video source provided.");
    }

    if (onProgress) onProgress(45);

    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

    if (onProgress) onProgress(75);

    const sampleRate = audioBuffer.sampleRate;
    const numberOfChannels = audioBuffer.numberOfChannels;

    let startFrame = 0;
    let endFrame = audioBuffer.length;
    if (useTimeRange && startTime !== undefined && endTime !== undefined) {
      startFrame = Math.floor(startTime * sampleRate);
      endFrame = Math.floor(endTime * sampleRate);
    }
    startFrame = Math.max(0, Math.min(startFrame, audioBuffer.length));
    endFrame = Math.max(startFrame + 1, Math.min(endFrame, audioBuffer.length));

    const channelData: Float32Array[] = [];
    for (let c = 0; c < numberOfChannels; c++) {
      channelData.push(audioBuffer.getChannelData(c).subarray(startFrame, endFrame));
    }

    const wavBuffer = this.encodeWAV(channelData, sampleRate, numberOfChannels);

    if (onProgress) onProgress(95);

    const ext = (outputFormat || 'MP3').toLowerCase();
    const mimeType = ext === 'wav' ? 'audio/wav' : 'audio/mp3';
    const blob = new Blob([wavBuffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const filename = `converted_audio.${ext}`;

    if (onProgress) onProgress(100);

    return { blob, url, filename };
  }
}
